import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
  AlertMetric,
  NightReportMetric,
} from "interfaces/nightReport.interface";

import { NightReportFieldCell } from "./NightReportFieldCell";

type DeltaSource = "deviation_pct" | "baseline";

const isDefined = <T,>(val: T | null | undefined): val is T => {
  return val !== null && val !== undefined;
};

interface MetricCellProps {
  metric: NightReportMetric | null | undefined;
  title: string;
  toolTip: string;
  belowThreshold: boolean;
  alertMetric?: AlertMetric;
  valueFormatter: (value: number) => string;
  deltaSource: DeltaSource;
  showPercent?: boolean;
  belowMissingFallback?: string;
  testId: string;
}

export function MetricCell({
  metric,
  title,
  toolTip,
  belowThreshold,
  alertMetric,
  valueFormatter,
  deltaSource,
  showPercent = true,
  belowMissingFallback,
  testId,
}: MetricCellProps) {
  const { t } = useTranslation();
  const naLabel = t("N/A");
  const valueText = useMemo(() => {
    if (!isDefined(metric?.value)) return naLabel;

    return valueFormatter(metric.value);
  }, [metric, valueFormatter, naLabel]);

  const belowValueText =
    belowThreshold && !isDefined(metric?.value)
      ? (belowMissingFallback ?? naLabel)
      : valueText;
  const delta =
    metric &&
    (deltaSource === "deviation_pct" ? metric.deviation_pct : metric.baseline);
  const badgeData =
    !belowThreshold && delta
      ? {
          showPercent: showPercent,
          delta: delta,
          alertMetric: alertMetric,
          metric: metric,
          testId: testId,
        }
      : null;

  return (
    <NightReportFieldCell
      cellText={belowValueText}
      testId={testId}
      title={title}
      toolTip={toolTip}
      metricBadgeData={badgeData}
    />
  );
}
